import React from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Headset, PhoneOff, User } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

// P0-D: GLOBAL incoming customer-handoff alert. Rendered once at the Business shell (WebRTCProvider) so a
// logged-in eligible operator rings on EVERY authenticated Business route — not only Operator Workspace.
// P0 (web accept/media): "Answer" now EXECUTES the canonical provider accept (winner-lock + media room),
// so audio connects from any route; it then navigates to the workspace for the full view. Tenant-safe only.
const IncomingHandoffModal = ({ incomingHandoff, onAccept, onDismiss }) => {
  const { t } = useLanguage();
  const location = useLocation();
  const [answering, setAnswering] = React.useState(false);
  if (!incomingHandoff) return null;
  // Belt-and-suspenders: the workspace owns its own queue/ring; never show the global alert there.
  if (location.pathname && location.pathname.indexOf('/unified-desktop') !== -1) return null;

  // UI-identity: show the tenant-resolved REAL customer name. Guard against any placeholder / raw identifier
  // that must never be shown AS the person's name (backend already resolves + sanitizes; this is defence-in-depth).
  const _rawName = (incomingHandoff.customer_display_name || incomingHandoff.customer_name || '').trim();
  const _isIdentifier = !_rawName
    || /^customer$/i.test(_rawName)
    || /^(cust_|customer_|anon_|guest)/i.test(_rawName)
    || /^\+?\d[\d\s()-]{6,}$/.test(_rawName);   // phone-like
  const resolvedName = _isIdentifier ? '' : _rawName;
  const name = resolvedName || 'Unknown customer';   // honest fallback; never "Customer"/raw id/phone
  const initials = (resolvedName.split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('') || '?').toUpperCase();
  const urgent = incomingHandoff.priority === 'urgent';

  const onAnswer = async () => {
    if (answering) return;                 // UI-level single-flight (provider also guards)
    setAnswering(true);
    // AUTOPLAY FIX: this trusted click grants document-level user-activation; the global
    // ActiveCallInterface <audio> sink then plays the customer automatically. We do NOT navigate —
    // navigating away dropped the activation and blocked autoplay (confirmed: clicking Enable sound
    // restored audio). The global call UI renders on the current route. "Enable sound" remains a fallback.
    try {
      if (onAccept) await onAccept(incomingHandoff);   // real accept: winner-lock → media room (answerer)
    } finally {
      setAnswering(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl">
        <div className="text-center">
          <div className="w-20 h-20 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4">
            {resolvedName
              ? <span className="text-2xl font-semibold text-white" aria-hidden="true">{initials}</span>
              : <User className="h-10 w-10 text-white" />}
          </div>

          <h3 className="text-xl font-semibold text-gray-900 mb-2" aria-label={`Incoming call from ${name}`}>{name}</h3>

          <div className="mb-4">
            <Badge className={`${urgent ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'} mb-2`}>
              <Headset className="h-3 w-3 mr-1" />
              {urgent ? 'Urgent — AI Voice handoff' : 'AI Voice handoff'}
            </Badge>
          </div>

          <p className="text-gray-600 mb-6">{`Incoming call from ${name} — answer to connect.`}</p>

          <div className="flex justify-center space-x-4">
            <Button
              onClick={() => onDismiss && onDismiss()}
              className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-6 py-3 rounded-full"
            >
              <PhoneOff className="h-5 w-5 mr-2" />
              Dismiss
            </Button>
            <Button
              onClick={onAnswer}
              disabled={answering}
              className="bg-emerald-500 hover:bg-emerald-600 text-white px-6 py-3 rounded-full disabled:opacity-60"
            >
              <Headset className="h-5 w-5 mr-2" />
              {answering ? 'Connecting…' : 'Answer'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IncomingHandoffModal;
